import { create } from 'zustand';
import type { ExcelData, DataRow, ExcelField, FlagColor } from '@/types';
import { EXCEL_FIELDS, DEFAULT_DESCRIPTION, FIRST_IMAGE_FIELD, DEFAULT_FLAG } from '@/lib/constants';
import { setFirstImage, getFirstImage } from '@/services/excelService';

interface DataState {
    // 原始数据 (导入时的快照，用于对比)
    originalRows: DataRow[];
    // 当前编辑数据
    rows: DataRow[];
    // 表头字段 (Excel 原始字段，不含虚拟字段)
    fields: ExcelField[];
    // 说明行文本
    description: string;
    // 文件名
    fileName: string;
    // 是否已导入数据
    hasData: boolean;
    // LLM 错误信息：rowIndex -> fieldKey -> errorMessage
    cellErrors: Record<number, Record<string, string>>;
    // 被过滤标记的行索引（低质量过滤后标记，显示灰色删除线，导出时排除）
    filteredRowIndices: Set<number>;
    // 图片原地替换映射：oldUrl → newUrl（编辑图片保存后记录，用于对比时识别「修改」而非「新增+删除」）
    imageReplacements: Map<string, string>;
    // 产品旗标状态：productId -> 旗子颜色（默认灰色，见 DEFAULT_FLAG）
    productFlags: Record<string, FlagColor>;

    // 导入数据
    importData: (data: ExcelData) => void;
    // 更新单个单元格 (自动处理 firstImage ↔ mainImages 同步)
    updateCell: (rowIndex: number, fieldKey: string, value: string) => void;
    // 记录图片原地替换
    recordImageReplacement: (oldUrl: string, newUrl: string) => void;
    clearImageReplacements: () => void;
    // 批量设置产品旗标状态（可同时设置多个 productId）
    setProductFlag: (productIds: string[], color: FlagColor) => void;
    // 设置单元格错误
    setCellError: (rowIndex: number, fieldKey: string, message: string) => void;
    // 清除单元格错误
    clearCellError: (rowIndex: number, fieldKey: string) => void;
    // 批量替换某列
    batchReplace: (fieldKey: string, findValue: string, replaceValue: string) => void;
    // 批量设置某列值
    batchSet: (fieldKey: string, value: string) => void;
    // 更新整行数据 (用于标题优化/OCR处理后回写，自动同步 firstImage)
    updateRow: (rowIndex: number, updates: Partial<DataRow>) => void;
    // 标记行已被过滤（显示灰色删除线，导出时排除）
    markRowsFiltered: (rowIndices: number[]) => void;
    // 取消行的过滤标记
    unmarkRowsFiltered: (rowIndices: number[]) => void;
    // 重置为原始数据
    resetToOriginal: () => void;
    // 清空数据
    clearData: () => void;
}

// 处理 firstImage ↔ mainImages 双向同步，返回需要应用到行的更新
function syncFirstImage(fieldKey: string, value: string, currentRow: DataRow): Partial<DataRow> {
    if (fieldKey === 'firstImage') {
        // 修改首图 → 同步更新产品主图第一个链接
        const newMainImages = setFirstImage(currentRow.mainImages ?? '', value);
        return { firstImage: value, mainImages: newMainImages };
    }
    if (fieldKey === 'mainImages') {
        // 修改产品主图 → 同步更新首图
        const newFirst = getFirstImage(value);
        return { mainImages: value, firstImage: newFirst };
    }
    return { [fieldKey]: value };
}

// 需要同产品内同步的字段：修改某一行的这些字段时，同 productId 的所有行都同步修改
const SYNCED_PRODUCT_FIELDS = new Set(['productName']);

// 对需要同产品同步的字段，将修改应用到所有同 productId 的行
function applyCellUpdate(
    rows: DataRow[], rowIndex: number, fieldKey: string, value: string,
): DataRow[] {
    const currentRow = rows[rowIndex];
    const updates = syncFirstImage(fieldKey, value, currentRow);

    // 如果是需要同产品同步的字段，找到所有同 productId 的行一起修改
    if (SYNCED_PRODUCT_FIELDS.has(fieldKey)) {
        const productId = (currentRow.productId ?? '').trim();
        if (productId) {
            return rows.map((row, i) => {
                if (row.productId === productId) {
                    const rowUpdates = syncFirstImage(fieldKey, value, row);
                    return { ...row, ...rowUpdates };
                }
                return row;
            });
        }
    }

    const newRows = [...rows];
    newRows[rowIndex] = { ...currentRow, ...updates };
    return newRows;
}

function formatOperationTime(date = new Date()): string {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function stampRelatedProducts(rows: DataRow[], rowIndices: number[], processedAt = formatOperationTime()): DataRow[] {
    if (rowIndices.length === 0) return rows;
    const productIds = new Set<string>();
    const standaloneIndices = new Set<number>();
    for (const index of rowIndices) {
        const productId = (rows[index]?.productId ?? '').trim();
        if (productId) productIds.add(productId);
        else standaloneIndices.add(index);
    }
    return rows.map((row, index) => {
        const productId = (row.productId ?? '').trim();
        return (productId && productIds.has(productId)) || standaloneIndices.has(index)
            ? { ...row, processedAt }
            : row;
    });
}

export const useDataStore = create<DataState>((set, get) => ({
    originalRows: [],
    rows: [],
    fields: EXCEL_FIELDS,
    description: DEFAULT_DESCRIPTION,
    fileName: '',
    hasData: false,
    cellErrors: {},
    filteredRowIndices: new Set(),
    imageReplacements: new Map(),
    productFlags: {},

    importData: (data: ExcelData) => {
        set({
            originalRows: data.rows.map(r => ({ ...r, processedAt: '' })),
            rows: data.rows.map(r => ({ ...r, processedAt: '' })),
            fields: data.headers.length > 0 ? data.headers : EXCEL_FIELDS,
            description: data.description || DEFAULT_DESCRIPTION,
            fileName: data.fileName,
            hasData: true,
            filteredRowIndices: new Set(),
            imageReplacements: new Map(),
            productFlags: {},
        });
    },

    updateCell: (rowIndex: number, fieldKey: string, value: string) => {
        const { rows, cellErrors } = get();
        if (rowIndex < 0 || rowIndex >= rows.length) return;
        const newRows = stampRelatedProducts(applyCellUpdate(rows, rowIndex, fieldKey, value), [rowIndex]);
        // 清除该单元格的错误（成功更新后）
        const newErrors = { ...cellErrors };
        if (newErrors[rowIndex]) {
            const { [fieldKey]: _, ...rest } = newErrors[rowIndex];
            if (Object.keys(rest).length === 0) {
                delete newErrors[rowIndex];
            } else {
                newErrors[rowIndex] = rest;
            }
        }
        set({ rows: newRows, cellErrors: newErrors });
    },

    recordImageReplacement: (oldUrl: string, newUrl: string) => {
        const { imageReplacements } = get();
        const next = new Map(imageReplacements);
        next.set(oldUrl, newUrl);
        set({ imageReplacements: next });
    },

    clearImageReplacements: () => {
        set({ imageReplacements: new Map() });
    },

    setProductFlag: (productIds: string[], color: FlagColor) => {
        if (productIds.length === 0) return;
        const { productFlags, rows } = get();
        const next = { ...productFlags };
        productIds.forEach(id => { next[id] = color; });
        const indices = rows.flatMap((row, index) => productIds.includes((row.productId ?? '').trim()) ? [index] : []);
        set({ productFlags: next, rows: stampRelatedProducts(rows, indices) });
    },

    setCellError: (rowIndex: number, fieldKey: string, message: string) => {
        const { cellErrors } = get();
        const newErrors = { ...cellErrors };
        newErrors[rowIndex] = { ...newErrors[rowIndex], [fieldKey]: message };
        set({ cellErrors: newErrors });
    },

    clearCellError: (rowIndex: number, fieldKey: string) => {
        const { cellErrors } = get();
        const newErrors = { ...cellErrors };
        if (newErrors[rowIndex]) {
            const { [fieldKey]: _, ...rest } = newErrors[rowIndex];
            if (Object.keys(rest).length === 0) {
                delete newErrors[rowIndex];
            } else {
                newErrors[rowIndex] = rest;
            }
        }
        set({ cellErrors: newErrors });
    },

    batchReplace: (fieldKey: string, findValue: string, replaceValue: string) => {
        const { rows } = get();
        // 对于需要同产品同步的字段，先收集要更新的 productId 和值
        if (SYNCED_PRODUCT_FIELDS.has(fieldKey)) {
            const productUpdates = new Map<string, string>();
            rows.forEach(row => {
                const val = row[fieldKey] ?? '';
                if (val.includes(findValue)) {
                    const productId = (row.productId ?? '').trim();
                    if (productId && !productUpdates.has(productId)) {
                        productUpdates.set(productId, val.split(findValue).join(replaceValue));
                    }
                }
            });
            const newRows = rows.map(row => {
                const productId = (row.productId ?? '').trim();
                const newVal = productUpdates.get(productId);
                if (newVal !== undefined) {
                    const updates = syncFirstImage(fieldKey, newVal, row);
                    return { ...row, ...updates };
                }
                return row;
            });
            const changedIndices = rows.flatMap((row, index) => row[fieldKey] !== newRows[index]?.[fieldKey] ? [index] : []);
            set({ rows: stampRelatedProducts(newRows, changedIndices) });
        } else {
            const newRows = rows.map(row => {
                const val = row[fieldKey] ?? '';
                if (val.includes(findValue)) {
                    const newVal = val.split(findValue).join(replaceValue);
                    const updates = syncFirstImage(fieldKey, newVal, row);
                    return { ...row, ...updates };
                }
                return row;
            });
            const changedIndices = rows.flatMap((row, index) => row[fieldKey] !== newRows[index]?.[fieldKey] ? [index] : []);
            set({ rows: stampRelatedProducts(newRows, changedIndices) });
        }
    },

    batchSet: (fieldKey: string, value: string) => {
        const { rows } = get();
        const newRows = rows.map(row => {
            const updates = syncFirstImage(fieldKey, value, row);
            return { ...row, ...updates };
        });
        const changedIndices = rows.flatMap((row, index) => row[fieldKey] !== newRows[index]?.[fieldKey] ? [index] : []);
        set({ rows: stampRelatedProducts(newRows, changedIndices) });
    },

    updateRow: (rowIndex: number, updates: Partial<DataRow>) => {
        const { rows } = get();
        if (rowIndex < 0 || rowIndex >= rows.length) return;
        const newRows = [...rows];
        const currentRow = newRows[rowIndex];
        let merged = { ...currentRow, ...updates };
        // 如果更新包含 mainImages，同步 firstImage
        if (updates.mainImages !== undefined) {
            merged.firstImage = getFirstImage(merged.mainImages ?? '');
        }
        // 如果更新包含 firstImage，同步 mainImages
        if (updates.firstImage !== undefined && updates.mainImages === undefined) {
            merged.mainImages = setFirstImage(currentRow.mainImages ?? '', updates.firstImage ?? '');
        }
        newRows[rowIndex] = merged;
        set({ rows: stampRelatedProducts(newRows, [rowIndex]) });
    },

    resetToOriginal: () => {
        const { originalRows } = get();
        set({ rows: originalRows.map(r => ({ ...r })), cellErrors: {}, filteredRowIndices: new Set(), imageReplacements: new Map() });
    },

    markRowsFiltered: (rowIndices: number[]) => {
        if (rowIndices.length === 0) return;
        const { filteredRowIndices, rows } = get();
        const newSet = new Set(filteredRowIndices);
        rowIndices.forEach(i => newSet.add(i));
        set({ filteredRowIndices: newSet, rows: stampRelatedProducts(rows, rowIndices) });
    },

    unmarkRowsFiltered: (rowIndices: number[]) => {
        if (rowIndices.length === 0) return;
        const { filteredRowIndices, rows } = get();
        const newSet = new Set(filteredRowIndices);
        rowIndices.forEach(i => newSet.delete(i));
        set({ filteredRowIndices: newSet, rows: stampRelatedProducts(rows, rowIndices) });
    },

    clearData: () => {
        set({
            originalRows: [],
            rows: [],
            fileName: '',
            hasData: false,
            cellErrors: {},
            filteredRowIndices: new Set(),
            imageReplacements: new Map(),
            productFlags: {},
        });
    },
}));

// 导出虚拟字段供组件使用
export { FIRST_IMAGE_FIELD };
